import {
  hasAllPermissions,
  hasAnyPermission,
  hasPermission,
  permissionsForRoles,
  type Permission,
  type Role,
} from '@fleetcore/shared'
import { useMemo } from 'react'

import { useAuthStore } from './auth-store'

const NO_ROLES: readonly Role[] = []

export interface UsePermissions {
  roles: readonly Role[]
  permissions: ReadonlySet<Permission>
  /** True when the signed-in user holds this permission. */
  can: (permission: Permission) => boolean
  canAny: (permissions: readonly Permission[]) => boolean
  canAll: (permissions: readonly Permission[]) => boolean
}

/**
 * Answers "should this be rendered", never "is this allowed".
 *
 * The matrix imported here is the same one the API enforces, but a client-side
 * check is only ever an affordance: hiding a button is a courtesy to the user,
 * not a control. Every action behind it is re-authorized server-side from the
 * roles in the signed token.
 */
export function usePermissions(): UsePermissions {
  const roles = useAuthStore((state) => state.user?.roles) ?? NO_ROLES

  return useMemo(
    () => ({
      roles,
      permissions: permissionsForRoles(roles),
      can: (permission: Permission) => hasPermission(roles, permission),
      canAny: (permissions: readonly Permission[]) => hasAnyPermission(roles, permissions),
      canAll: (permissions: readonly Permission[]) => hasAllPermissions(roles, permissions),
    }),
    [roles],
  )
}
